/**
 * API client.
 *
 * One place that knows how to talk to the Function App: base URL, auth header,
 * and error shape. Everything else in the app calls typed helpers.
 */

import type {
  AuthPrincipal,
  Category,
  Discount,
  NavSettings,
  ShippingProfile,
  StoreSettings,
  ThemeSettings,
} from '@gills/shared';

// In development Vite proxies /api to the Functions host, so a relative base
// keeps the browser on one origin and avoids CORS. In production Static Web
// Apps serves the linked Function App under the same hostname, so the same
// relative path is correct there too.
const BASE = '/api';

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/** Set by the auth provider so requests carry the current token. */
let tokenProvider: (() => Promise<string | null>) | null = null;

export function setTokenProvider(provider: (() => Promise<string | null>) | null): void {
  tokenProvider = provider;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Send the bearer token even on endpoints that also accept guests. */
  auth?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  // Attach the token whenever we have one. Public endpoints ignore it.
  //
  // Deliberately NOT the Authorization header: Static Web Apps reserves it when
  // proxying to managed Functions and replaces whatever we send with its own
  // internal invocation token, so anything put there never reaches the API.
  if (tokenProvider) {
    const token = await tokenProvider().catch(() => null);
    if (token) headers['x-gills-authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { message: text };
    }
  }

  if (!response.ok) {
    const body = payload as { message?: string; error?: string; details?: unknown } | null;
    throw new ApiClientError(
      response.status,
      body?.message ?? `Request failed (${response.status}).`,
      body?.error ?? 'error',
      body?.details,
    );
  }

  return payload as T;
}

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

export interface ImageDto {
  id: string;
  alt: string;
  width: number;
  height: number;
  placeholder: string | null;
  srcset: string;
  src: string;
}

export interface ProductCardDto {
  id: string;
  slug: string;
  title: string;
  productType: string;
  categoryIds: string[];
  tags: string[];
  images: ImageDto[];
  priceMinor: number;
  compareAtMinor: number | null;
  saleTitle: string | null;
  currency: string;
  stock: number;
  inStock: boolean;
  seo: { title: string; description: string };
  publishedAt: string | null;
}

export interface ProductDto extends ProductCardDto {
  descriptionHtml: string;
  descriptionText: string;
}

export interface FacetBucket {
  value: string;
  label: string;
  count: number;
}

export interface ProductListResponse {
  products: ProductCardDto[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  facets: {
    tags: FacetBucket[];
    productTypes: FacetBucket[];
    priceRange: { minMinor: number; maxMinor: number };
    onSaleCount: number;
    inStockCount: number;
  };
}

export interface CategorySummary {
  id: string;
  slug: string;
  title: string;
  description: string;
  menuGroup: string;
  isSaleSection: boolean;
  productCount: number;
}

export interface StorefrontResponse {
  store: StoreSettings;
  theme: ThemeSettings;
  nav: NavSettings;
  categories: CategorySummary[];
  activeSales: Array<{ id: string; title: string; description: string }>;
}

export interface ShippingQuoteDto {
  rateId: string;
  name: string;
  description: string;
  basePriceMinor: number;
  priceMinor: number;
  freeByThreshold: boolean;
  minDeliveryDays: number;
  maxDeliveryDays: number;
  zoneName: string;
}

export interface CartLineDto {
  productId: string;
  slug: string;
  title: string;
  image: ImageDto | null;
  unitPriceMinor: number;
  compareAtMinor: number | null;
  quantity: number;
  lineSubtotalMinor: number;
  lineDiscountMinor: number;
  lineTotalMinor: number;
  discounts: Array<{ discountId: string; title: string; code: string | null; amountMinor: number }>;
  availableStock: number;
  exceedsStock: boolean;
}

export interface CartDto {
  id: string;
  items: Array<{ productId: string; quantity: number }>;
  couponCode: string | null;
  email: string | null;
  shippingCountry: string | null;
  selectedShippingRateId: string | null;
  totals: {
    lines: CartLineDto[];
    subtotalMinor: number;
    discountMinor: number;
    shippingMinor: number;
    shippingDiscountMinor: number;
    totalMinor: number;
    currency: 'GBP' | 'EUR' | 'USD';
    appliedDiscounts: Array<{ discountId: string; title: string; code: string | null; amountMinor: number }>;
    couponError: string | null;
    totalQuantity: number;
    totalWeightGrams: number;
    shippingQuotes: ShippingQuoteDto[];
    selectedShippingRateId: string | null;
  };
}

export interface OrderDto {
  id: string;
  orderNumber: string;
  status: string;
  email: string;
  lines: Array<{
    productId: string;
    slug: string;
    title: string;
    imageUrl: string | null;
    unitPriceMinor: number;
    quantity: number;
    lineTotalMinor: number;
  }>;
  subtotalMinor: number;
  discountMinor: number;
  shippingMinor: number;
  shippingDiscountMinor: number;
  totalMinor: number;
  currency: 'GBP' | 'EUR' | 'USD';
  appliedDiscounts: Array<{ title: string; amountMinor: number }>;
  shippingAddress: Record<string, string>;
  shippingRateName: string;
  payment: { provider: string; status: string; paidAt: string | null };
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Storefront
// ---------------------------------------------------------------------------

function toQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '' || value === false) continue;
    if (Array.isArray(value)) {
      if (value.length) search.set(key, value.join(','));
    } else {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export const api = {
  storefront: () => request<StorefrontResponse>('/storefront'),

  products: (params: Record<string, unknown> = {}) =>
    request<ProductListResponse>(`/products${toQuery(params)}`),

  product: (slug: string) =>
    request<{
      product: ProductDto;
      categories: Array<{ id: string; slug: string; title: string }>;
      related: ProductCardDto[];
    }>(`/products/${encodeURIComponent(slug)}`),

  category: (slug: string) =>
    request<{ category: CategorySummary & { seo: { title: string; description: string } } }>(
      `/categories/${encodeURIComponent(slug)}`,
    ),

  // -- Cart ------------------------------------------------------------------
  createCart: () => request<CartDto>('/cart', { method: 'POST' }),
  getCart: (id: string) => request<CartDto>(`/cart/${id}`),
  updateCart: (id: string, body: Record<string, unknown>) =>
    request<CartDto>(`/cart/${id}`, { method: 'PATCH', body }),
  addToCart: (id: string, productId: string, quantity = 1) =>
    request<CartDto>(`/cart/${id}/items`, { method: 'POST', body: { productId, quantity } }),
  removeFromCart: (id: string, productId: string) =>
    request<CartDto>(`/cart/${id}/items/${productId}`, { method: 'DELETE' }),

  // -- Shipping --------------------------------------------------------------
  shippingCountries: () =>
    request<{ countries: string[]; shipsWorldwide: boolean }>('/shipping/countries'),
  shippingQuote: (countryCode: string, cartId?: string) =>
    request<{ countryCode: string; quotes: ShippingQuoteDto[]; shipsToCountry: boolean }>(
      '/shipping/quote',
      { method: 'POST', body: { countryCode, cartId } },
    ),

  // -- Checkout --------------------------------------------------------------
  checkout: (body: Record<string, unknown>) =>
    request<{ order: OrderDto; clientSecret: string; paymentProvider: string }>('/checkout', {
      method: 'POST',
      body,
    }),
  confirmOrder: (orderId: string) =>
    request<{ order: OrderDto }>(`/checkout/${orderId}/confirm`, { method: 'POST' }),
  order: (orderId: string, email?: string) =>
    request<{ order: OrderDto }>(`/orders/${orderId}${toQuery({ email })}`),
  lookupOrder: (orderNumber: string, email: string) =>
    request<{ order: OrderDto }>('/orders/lookup', {
      method: 'POST',
      body: { orderNumber, email },
    }),

  // -- Account ---------------------------------------------------------------
  me: () =>
    request<{ principal: AuthPrincipal | null; tokenError?: string | null }>('/me'),
  myOrders: () => request<{ orders: OrderDto[] }>('/me/orders'),

  // -- Auth ------------------------------------------------------------------
  authConfig: () =>
    request<
      | { mode: 'dev' }
      | { mode: 'unconfigured'; missing: string[] }
      | {
          mode: 'entra';
          clientId: string;
          authority: string;
          knownAuthorities: string[];
          apiScope: string;
        }
    >('/auth/config'),
  devLogin: (email: string, name: string, isAdmin: boolean) =>
    request<{ token: string; principal: AuthPrincipal }>('/auth/dev-login', {
      method: 'POST',
      body: { email, name, isAdmin },
    }),
};

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

/**
 * Admin endpoints live under /api/manage, not /api/admin: the Azure Functions
 * host reserves the `admin/*` route template for its own management API and
 * rejects those routes at startup. The browser path is still /admin.
 */
export interface AdminOverview {
  products: { total: number; active: number; draft: number; outOfStock: number };
  categories: number;
  discounts: { total: number; enabled: number };
  orders: {
    total: number;
    awaitingPayment: number;
    toFulfil: number;
    revenueMinor: number;
  };
  recentOrders: AdminOrderDto[];
}

/**
 * Admin order and product payloads stay loosely typed on purpose: they mirror
 * the stored documents, which the admin screens edit field by field, and a
 * duplicate of every domain type here would drift from the server's.
 */
export type AdminOrderDto = OrderDto & {
  customerId: string | null;
  payment: { provider: string; status: string; paidAt: string | null; reference: string | null };
  notes: string;
  shippingAddress: Record<string, string>;
};

export const adminApi = {
  overview: () => request<AdminOverview>('/manage/overview'),

  products: (q?: string) =>
    request<{ products: any[]; total: number }>(`/manage/products${toQuery({ q })}`),
  product: (id: string) => request<{ product: any }>(`/manage/products/${id}`),
  createProduct: (body: unknown) =>
    request<{ product: any }>('/manage/products', { method: 'POST', body }),
  updateProduct: (id: string, body: unknown) =>
    request<{ product: any }>(`/manage/products/${id}`, { method: 'PUT', body }),
  deleteProduct: (id: string) =>
    request<void>(`/manage/products/${id}`, { method: 'DELETE' }),
  imageUploadUrl: (id: string, contentType: string, width: number) =>
    request<{ imageId: string; path: string; uploadUrl: string; publicUrl: string }>(
      `/manage/products/${id}/image-upload-url`,
      { method: 'POST', body: { contentType, width } },
    ),

  categories: () => request<{ categories: (Category & { productCount: number })[] }>('/manage/categories'),
  saveCategory: (id: string | null, body: unknown) =>
    request<{ category: Category }>(`/manage/categories${id ? `/${id}` : ''}`, {
      method: id ? 'PUT' : 'POST',
      body,
    }),
  deleteCategory: (id: string) =>
    request<{ deleted: boolean; productsUpdated: number }>(`/manage/categories/${id}`, {
      method: 'DELETE',
    }),

  discounts: () => request<{ discounts: Discount[] }>('/manage/discounts'),
  saveDiscount: (id: string | null, body: unknown) =>
    request<{ discount: Discount }>(`/manage/discounts${id ? `/${id}` : ''}`, {
      method: id ? 'PUT' : 'POST',
      body,
    }),
  deleteDiscount: (id: string) => request<void>(`/manage/discounts/${id}`, { method: 'DELETE' }),

  shipping: () => request<{ profiles: ShippingProfile[] }>('/manage/shipping'),
  saveShipping: (id: string, body: unknown) =>
    request<{ profile: ShippingProfile }>(`/manage/shipping/${id}`, { method: 'PUT', body }),

  orders: (status?: string) =>
    request<{ orders: any[]; total: number }>(`/manage/orders${toQuery({ status })}`),
  updateOrder: (id: string, body: unknown) =>
    request<{ order: any }>(`/manage/orders/${id}`, { method: 'PATCH', body }),

  saveSettings: (key: 'theme' | 'store' | 'nav', body: unknown) =>
    request<{ settings: any }>(`/manage/settings/${key}`, { method: 'PUT', body }),
};
